import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { reportApi, clientApi } from '@/services/api'
import type { BalanceSheet as BalanceSheetType, ReportAccountLine } from '@/types'
import { formatCurrency } from '@/utils/currency'
import { FileText, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

function SectionBlock({ title, headerClass, accounts, total, currency }: {
  title: string
  headerClass: string
  accounts: ReportAccountLine[]
  total: string
  currency: string
}) {
  return (
    <div className="border-b border-border">
      <div className={`px-5 py-2.5 border-b border-border ${headerClass}`}>
        <p className="text-[13px] font-bold uppercase tracking-wider">{title}</p>
      </div>
      <table className="w-full">
        <tbody>
          {accounts.map((acc) => (
            <tr key={acc.account_id || acc.account_code} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
              <td className="px-5 py-2.5 text-[13px] text-[rgba(0,10,30,0.75)] font-mono w-20">{acc.account_code}</td>
              <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{acc.account_name}</td>
              <td className="px-5 py-2.5 text-[13px] text-right tabular-nums text-[#000A1E]">{formatCurrency(acc.net, currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-muted/30">
            <td colSpan={2} className="px-5 py-3 text-[13px] font-bold text-[#000A1E]">Total {title}</td>
            <td className="px-5 py-3 text-[13px] font-bold text-right tabular-nums text-[#000A1E]">{formatCurrency(total, currency)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

export default function BalanceSheet() {
  const { clientId } = useParams<{ clientId: string }>()
  const [report, setReport] = useState<BalanceSheetType | null>(null)
  const [loading, setLoading] = useState(false)
  const [asAt, setAsAt] = useState(() => new Date().toISOString().split('T')[0])
  const [exporting, setExporting] = useState(false)
  const [currency, setCurrency] = useState('USD')

  useEffect(() => {
    if (clientId) clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    generate()
  }, [clientId])

  async function generate() {
    if (!clientId) return
    setLoading(true)
    try {
      const res = await reportApi.balanceSheet(clientId, asAt)
      setReport(res.data)
    } finally { setLoading(false) }
  }

  async function handleExport() {
    if (!clientId) return
    setExporting(true)
    try {
      const res = await reportApi.exportPdf(clientId, 'balance-sheet', { as_at: asAt })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `balance-sheet-${asAt}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Balance Sheet</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Assets, liabilities and equity</p>
        </div>
        {report && (
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>As at</Label>
              <Input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} className="w-auto" />
            </div>
            <Button onClick={generate} disabled={loading}>
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      ) : report ? (
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">As at {report.as_at}</p>
          </div>
          <SectionBlock
            title="Assets"
            headerClass="bg-blue-50/50 text-blue-700"
            accounts={report.assets.accounts}
            total={report.assets.total}
            currency={currency}
          />
          <SectionBlock
            title="Liabilities"
            headerClass="bg-amber-50/50 text-amber-700"
            accounts={report.liabilities.accounts}
            total={report.liabilities.total}
            currency={currency}
          />
          <SectionBlock
            title="Equity"
            headerClass="bg-purple-50/50 text-purple-700"
            accounts={report.equity.accounts}
            total={report.equity.total}
            currency={currency}
          />
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Select a date and click Generate</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
